/**
 * Light-touch logging utility for observability
 * All logging is best-effort and non-blocking
 */

// ---- config
const ENABLE_REMOTE_LOGS =
  (import.meta.env.VITE_ENABLE_REMOTE_LOGS ?? "false").toLowerCase() === "true";

// Example:
//  Dev (Supabase local):  http://127.0.0.1:54321/functions/v1
//  Cloud:                 https://<project-ref>.functions.supabase.co
const EDGE_BASE_URL = import.meta.env.VITE_EDGE_BASE_URL?.replace(/\/$/, "");
const LOG_URL = ENABLE_REMOTE_LOGS && EDGE_BASE_URL ? `${EDGE_BASE_URL}/log_event` : undefined;

export type LogPayload = {
  level: 'info' | 'warn' | 'error' | 'critical';
  source: 'web' | 'edge' | 'rpc' | 'db';
  code?: string;
  message: string;
  route?: string;
  user_id?: string;
  session_id?: string;
  context?: Record<string, unknown>;
};

/**
 * Safe logging function that NEVER throws
 * This is fire-and-forget and will never break the app
 */
export async function safeLog(event: LogPayload): Promise<void> {
  if (!ENABLE_REMOTE_LOGS || !LOG_URL) {
    if (import.meta.env.DEV) console.debug('[log] remote logging disabled');
    return; // silently noop
  }

  try {
    // Add user context if available
    const enrichedPayload = {
      ...event,
      user_id: getCurrentUserId(),
      session_id: sessionStorage.getItem('session_id') || undefined,
      route: getCurrentRoute(),
      timestamp: new Date().toISOString(),
    };

    await fetch(LOG_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // For Supabase Edge Functions, anon key works for public endpoint:
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY ?? ""}`,
      },
      body: JSON.stringify(enrichedPayload),
      keepalive: true,
      mode: "cors",
    });
  } catch (err) {
    // Never rethrow — logging must be best-effort only.
    if (import.meta.env.DEV) console.debug("[log] remote log failed (silenced)", err);
  }
}

/**
 * Legacy logEvent function for backward compatibility
 */
export async function logEvent(payload: LogPayload): Promise<void> {
  await safeLog(payload);
}

/**
 * Convenience functions for common log types
 */
export const logger = {
  info: (message: string, context?: Record<string, unknown>) => 
    safeLog({ level: 'info', source: 'web', message, context }),
    
  warn: (message: string, context?: Record<string, unknown>) => 
    safeLog({ level: 'warn', source: 'web', message, context }),
    
  error: (message: string, context?: Record<string, unknown>) => 
    safeLog({ level: 'error', source: 'web', message, context }),
    
  critical: (message: string, context?: Record<string, unknown>) => 
    safeLog({ level: 'critical', source: 'web', message, context }),
    
  rpc: (code: string, message: string, context?: Record<string, unknown>) => 
    safeLog({ level: 'error', source: 'rpc', code, message, context }),
    
  edge: (code: string, message: string, context?: Record<string, unknown>) => 
    safeLog({ level: 'error', source: 'edge', code, message, context }),
};

/**
 * Get current route for logging
 */
export function getCurrentRoute(): string {
  return window.location.pathname;
}

/**
 * Get current user ID for logging (if available)
 */
export function getCurrentUserId(): string | undefined {
  // This will be populated by the auth context
  return (window as any).__currentUserId;
}

/**
 * Set current user ID for logging
 */
export function setCurrentUserId(userId: string | null): void {
  (window as any).__currentUserId = userId;
}